import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, User as UserIcon, Mail, Phone as PhoneIcon, Shield, Check } from "lucide-react";
import api from "../../lib/api";

/**
 * EstateOS — Çalışanlar (Users) management.
 * Reuses the existing users collection; adds department + estate_role_id + phone.
 */

const EstateUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [u, r] = await Promise.all([
        api.get("/estateos/users"),
        api.get("/estateos/roles"),
      ]);
      setUsers(u.data || []);
      setRoles(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ new: true }); setForm(empty()); };
  const startEdit = (u) => { setEditing(u); setForm({ ...empty(), ...u, password: "" }); };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (editing?.new) await api.post("/estateos/users", payload);
      else await api.put(`/estateos/users/${editing.id}`, payload);
      toast.success("Kaydedildi");
      cancel(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.name} silinsin mi?`)) return;
    try { await api.delete(`/estateos/users/${u.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const roleName = (id) => (roles.find((r) => r.id === id) || {}).name || "—";

  return (
    <div className="p-6 md:p-10" data-testid="estateos-users-page">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-primary font-medium">Yönetim</div>
          <h1 className="font-serif text-3xl text-brand-navy leading-tight">Çalışanlar</h1>
          <p className="text-brand-medium mt-2 text-sm">Sisteme erişecek kullanıcıları yönetin, rol ve departman atayın.</p>
        </div>
        <button onClick={startNew} className="btn-primary px-5 py-2.5 text-xs" data-testid="new-user-btn">
          <Plus size={14} strokeWidth={1.5} /> Yeni Çalışan
        </button>
      </header>

      <div className="bg-white border border-black/5">
        <table className="w-full text-sm">
          <thead className="bg-brand-light">
            <tr className="text-left text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium">
              <th className="px-4 py-3">Ad Soyad</th>
              <th className="px-4 py-3">E-posta</th>
              <th className="px-4 py-3">Telefon</th>
              <th className="px-4 py-3">Departman</th>
              <th className="px-4 py-3">Rol</th>
              <th className="px-4 py-3">Durum</th>
              <th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-brand-medium">Henüz çalışan yok.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-t border-black/5 hover:bg-brand-light/50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-brand-navy text-white text-xs inline-flex items-center justify-center">
                      {(u.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <span className="font-medium text-brand-navy">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-brand-medium">{u.email}</td>
                <td className="px-4 py-3 text-brand-medium">{u.phone || "—"}</td>
                <td className="px-4 py-3 text-brand-medium">{u.department || "—"}</td>
                <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 bg-brand-navy/5 text-brand-navy text-xs">{roleName(u.estate_role_id)}</span></td>
                <td className="px-4 py-3">
                  {u.is_active !== false ? (
                    <span className="inline-flex items-center gap-1 text-green-700 text-xs"><Check size={12} strokeWidth={1.5} /> Aktif</span>
                  ) : (
                    <span className="text-red-600 text-xs">Pasif</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => startEdit(u)} className="text-brand-navy hover:text-brand-primary p-1 mr-1" data-testid={`edit-user-${u.id}`}>
                    <Pencil size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(u)} className="text-red-600 hover:text-red-700 p-1" data-testid={`delete-user-${u.id}`}>
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="user-modal">
          <div className="absolute inset-0 bg-black/40" onClick={cancel} />
          <form onSubmit={save} className="relative bg-white w-full max-w-lg shadow-2xl">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-primary font-medium">Çalışan</div>
                <div className="font-serif text-lg text-brand-navy">{editing?.new ? "Yeni Çalışan" : form.name}</div>
              </div>
              <button type="button" onClick={cancel} className="text-brand-medium hover:text-brand-navy"><X size={18} strokeWidth={1.5} /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <Row icon={UserIcon} label="Ad Soyad"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" data-testid="user-name" /></Row>
              <Row icon={Mail} label="E-posta"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" data-testid="user-email" /></Row>
              <Row icon={PhoneIcon} label="Telefon"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" /></Row>
              <div className="grid grid-cols-2 gap-4">
                <Row label="Departman"><input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="inp" placeholder="Satış / Pazarlama / ..." /></Row>
                <Row icon={Shield} label="Rol">
                  <select value={form.estate_role_id || ""} onChange={(e) => setForm({ ...form, estate_role_id: e.target.value })} className="inp" data-testid="user-role">
                    <option value="">— Seçilmedi —</option>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </Row>
              </div>
              <Row label={editing?.new ? "Şifre" : "Şifre (değişiklik için doldurun)"}>
                <input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="inp" data-testid="user-password" placeholder={editing?.new ? "Zorunlu" : "Boş bırakırsanız değişmez"} />
              </Row>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <span>Aktif</span>
              </label>
            </div>
            <div className="px-6 py-4 bg-brand-light flex items-center justify-end gap-2 border-t border-black/10">
              <button type="button" onClick={cancel} className="text-xs uppercase tracking-[0.2em] text-brand-medium px-4 py-2">İptal</button>
              <button type="submit" disabled={busy} className="btn-primary px-6 py-2" data-testid="user-save">{busy ? "..." : "Kaydet"}</button>
            </div>
          </form>
        </div>
      ) : null}

      <style>{`.inp { width:100%; border:1px solid rgba(0,0,0,.1); padding:0.5rem 0.65rem; font-size:0.85rem; outline:none; } .inp:focus { border-color:#22346B; }`}</style>
    </div>
  );
};

const Row = ({ icon: Icon, label, children }) => (
  <label className="block">
    <div className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-medium font-medium mb-1.5 inline-flex items-center gap-1">
      {Icon ? <Icon size={10} strokeWidth={1.5} /> : null}{label}
    </div>
    {children}
  </label>
);

const empty = () => ({
  name: "", email: "", phone: "", department: "",
  estate_role_id: "", password: "", is_active: true, role: "admin",
});

export default EstateUsersPage;
